import React, { useState, useEffect, Fragment } from "react";
import { useDispatch } from "react-redux";
import { ReactSVG } from "react-svg";

import { PlusIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";

import PhysicianCreate from "./PhysicianCreate";
import Alert from "../../../common/Alert";
import { deleteCareproviderDetails } from "../../../../redux/modules/patient/actions";
import {
  formatPhone,
  capitalizeFirstLetter,
} from "../../../../helpers/functions";
import { designationOptions, stateOptions } from "../../create/options";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";

const PhysicianInformation = ({ patient }) => {
  const dispatch = useDispatch();
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [create, setCreate] = useState(false);
  const [currentPhysician, setCurrentPhysician] = useState(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [enableLeftArrow, setEnableLeftArrow] = useState(false);
  const [noCareProvider, setNoCareProvider] = useState(
    !(patient.careProvider && patient.careProvider.length > 0)
  );
  const [buttonHeight, setButtonHeight] = useState(550);

  const updateButtonHeight = (element) => {
    if (element) setButtonHeight(element.offsetHeight || 550);
  };

  useEffect(() => {
    const handleResize = () => {
      const firstCard = document.querySelector('.mainCard');
      if (firstCard) updateButtonHeight(firstCard);
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [patient.careProvider, showCreateSlider]);

  const handleLeftScroll = () => {
    const element = document.getElementById("physician-information");
    element.scrollLeft -= 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const [enableRightArrow, setEnableRightArrow] = useState(false);
  const handleRightScroll = () => {
    const element = document.getElementById("physician-information");
    element.scrollLeft += 400;
    setEnableRightArrow(
      Math.ceil(element.scrollLeft) + element.clientWidth !==
        element.scrollWidth
    );
    setEnableLeftArrow(element.scrollLeft !== 0);
  };

  const deletePhysician = (id) => {
    dispatch(deleteCareproviderDetails(patient.id, id));
  };
  const [idToDelete, setIdToDelete] = useState(null);
  const [openDeletePhysician, setOpenDeletePhysician] = useState(false);
  const physicianValueLabelOptions = [{ value: "none", label: "None" }].concat(
    patient.careProvider.map((option) => ({
      value: option.id,
      label:
        "Dr. " +
        option.firstName +
        " " +
        (option.middleName?.length > 0 ? " " : "") +
        option.lastName,
    }))
  );

  useEffect(() => {
    // Access the DOM element using the current property of the ref
    const myElement = document.getElementById("physician-information");
    if (myElement) {
      setIsOverflowing(myElement.scrollWidth > myElement.clientWidth);
      setEnableRightArrow(true);
    }
    setNoCareProvider(
      !(patient.careProvider && patient.careProvider.length > 0)
    );
  }, [patient.careProvider, isOverflowing]);

  return (
    <div className="relative w-full">
      {noCareProvider && (
        <Alert
          title="No Primary Provider information"
          type="warning"
          className="mb-4"
          isWrapped={true}
          showTexture={false}
          handleClose={() => setNoCareProvider(false)}
        />
      )}
      <div
        id="physician-information"
        className="flex gap-[30px] overflow-x-auto h-full min-h-[550px] scroll-smooth"
      >
        {patient.careProvider.map((item, i) => {
          const contactInfo = item.contactInfo;
          const physician = { ...item, ...item.contactInfo };
          return (
            <div
              key={i}
              ref={i === 0 ? (el) => updateButtonHeight(el) : null}
              className="mainCard min-w-[450px] w-auto flex-none"
            >
              {!create && (
                <PhysicianCreate
                  open={currentPhysician === item.id && showCreateSlider}
                  onClose={(value) => {
                    setShowCreateSlider(value);
                  }}
                  physician={physician}
                  patient={patient}
                />
              )}
              <div className="flex items-center justify-between h-[72px] px-6 py-3 border-b border-gray-100 bg-gray-20">
                <div className="flex flex-col gap-1">
                  <h4 className="text-lg font-bold leading-6 text-teal-600">
                    Dr. {capitalizeFirstLetter(item.firstName)}{" "}
                    {capitalizeFirstLetter(item.lastName)}
                  </h4>
                </div>
                <div>
                  <Menu as="div" className="relative inline-block text-left">
                    <div>
                      <Menu.Button>
                        <ReactSVG
                          src={`/icons/triple-dot.svg`}
                          className={`flex text-black !items-center w-6 scale-x-[1.4] scale-y-[1.4]`}
                        />
                      </Menu.Button>
                    </div>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform translate-y-2 opacity-0 scale-95"
                      enterTo="transform translate-y-0 opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform translate-y-0 opacity-100 scale-100"
                      leaveTo="transform translate-y-2 opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                        <Menu.Item>
                          <button
                            className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                            onClick={() => {
                              setShowCreateSlider(true);
                              setCreate(false);
                              setCurrentPhysician(item.id);
                            }}
                          >
                            Edit
                          </button>
                        </Menu.Item>
                        <Menu.Item>
                          <button
                            className="block text-left w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] border-b border-b-neutral-70 hover:bg-gray-50"
                            onClick={() => {
                              setOpenDeletePhysician(true);
                              setIdToDelete(item.id);
                            }}
                          >
                            Delete
                          </button>
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>
              <div className="px-6 py-3.5 flex flex-col h-full">
                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto pr-2">
                  <div className="grid grid-cols-1">
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Designation</p>
                    <div className="flex">
                      <p className="text-body-md font-bold text-gray-800 relative top-0.5">
                        {designationOptions.find(
                          (option) => option.value === item.title
                        )?.label || item.title}{" "}
                        &nbsp;
                      </p>
                      <p>
                        {item.primaryProvider && (
                          <span
                            className={`px-2 py-1 w-max font-medium bg-teal-75 text-teal-300 text-[10px] border rounded-full`}
                          >{`Primary Provider`}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">
                      License Number
                    </p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {item.licenseNumber || ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">
                      Primary Phone Number
                    </p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {contactInfo.primaryPhone ? formatPhone(contactInfo.primaryPhone) : ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Email</p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {contactInfo?.email || ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div>
                    <p className="mb-1.5 text-sm text-gray-700">Address</p>
                    <p className="font-bold text-gray-800 text-body-md">
                      {contactInfo?.address
                        ? `${contactInfo?.address}${contactInfo?.poBox ? `, PO Box: ${contactInfo?.poBox}` : ""}${contactInfo?.unitNumber ? `, Unit # ${contactInfo?.unitNumber}` : ""}`
                        : ""}
                    </p>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div className="grid grid-cols-2 justify-items-start">
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">
                        City/Town
                      </p>
                      <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                        {contactInfo?.city || ""}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Province</p>
                      <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                        {stateOptions.find(
                          (option) =>
                            option.value === contactInfo?.province
                        )?.label || ""}
                      </p>
                    </div>
                  </div>
                  <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                  <div className="grid grid-cols-2 justify-items-start">
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">
                        Postal Code
                      </p>
                      <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                        {contactInfo?.postalCode 
                          ? contactInfo.postalCode.replace(/\s/g, '').replace(/(.{3})(.*)/, '$1 $2').trim()
                          : ""}
                      </p>
                    </div>
                    <div>
                      <p className="mb-1.5 text-sm text-gray-700">Country</p>
                      <p className="font-bold text-gray-800 text-body-md whitespace-nowrap">
                        {contactInfo?.country ? capitalizeFirstLetter(contactInfo.country) : ""}
                      </p>
                    </div>
                  </div>
                  </div>
                </div>
                
                <div className="flex-shrink-0 border-t border-gray-200 pt-3 mt-3">
                  <div className="col-span-full">
                    <p className="mb-1.5 text-sm text-gray-700">Comments</p>
                    <p className="text-body-md font-bold text-gray-800 capitalize min-h-[22px]">
                      {item.notes || ""}
                    </p>
                  </div>
                </div>
              </div>
              <ConfirmationModalNew
                confirmText="Confirm"
                type="primary"
                title="Delete Primary Provider"
                optionLabel="Primary Provider Name"
                content={() =>
                  "Are you sure you want to delete this Primary Provider?"
                }
                confirmAction={() => {
                  deletePhysician(idToDelete);
                  setOpenDeletePhysician(false);
                  setIdToDelete(null);
                }}
                open={openDeletePhysician}
                handleClose={() => setOpenDeletePhysician(false)}
                onCross={() => setOpenDeletePhysician(false)}
                options={physicianValueLabelOptions}
                selectedOption={idToDelete}
              />
            </div>
          );
        })}
        {
          <button
            onClick={() => {
              setShowCreateSlider(true);
              setCreate(true);
            }}
            className="inline-flex justify-center items-center flex-col min-w-[450px] w-auto flex-none p-4 align-top bg-white border border-dashed rounded border-REELTeal text-lg font-medium text-REELTeal"
            style={{ height: `${buttonHeight}px`, minHeight: `${buttonHeight}px` }}
          >
            <PlusIcon className="w-6 h-6 text-REELTeal" />
            <span className="font-bold text-REELTeal text-body-md mt-2">
              Add Primary Provider
            </span>
          </button>
        }
        {isOverflowing && (
          <>
            {enableLeftArrow && (
              <button
                className="absolute m-auto left-0 bg-teal-75 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                onClick={handleLeftScroll}
              >
                <ReactSVG
                  src="/icons/arrow-right.svg"
                  className="w-[15px] rotate-180"
                />
              </button>
            )}
            {enableRightArrow && (
              <button
                className="absolute m-auto right-0 bg-teal-75 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                onClick={handleRightScroll}
              >
                <ReactSVG src="/icons/arrow-right.svg" className="w-[15px]" />
              </button>
            )}
          </>
        )}
        {create && (
          <PhysicianCreate
            open={showCreateSlider}
            onClose={(value) => {
              setShowCreateSlider(value);
            }}
            patient={patient}
            create={create}
          />
        )}
      </div>
    </div>
  );
};

export default PhysicianInformation;
